import { Card, CleanableInput } from "@caprikorn/dynamo-ui";
import { type FC, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { usePostPointsOfDeliveryFilter } from "@services/apiEcms/usePostPointsOfDeliveryFilter";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PointOfDelivery.module.css";
import type {
  PointOfDeliveryFormSubmitArguments,
  PointOfDeliveryProps,
} from "./PointOfDeliveryForm.types";
import type { PointOfDeliveryFormValues } from "../SearchDrawer.types";
import { useParsePointsOfDeliveryResponse } from "../SearchDrawer.utils";
import { SearchResults } from "../SearchResults/SearchResults";

export const PointOfDeliveryForm: FC<PointOfDeliveryProps> = ({
  toggleDrawer,
  formId,
}) => {
  const { t } = useCustomTranslation("main");

  const { handleSubmit, getValues, control } =
    useForm<PointOfDeliveryFormValues>({
      mode: FORM_VALIDATION_MODE,
      defaultValues: {
        pointOfDeliveryNumber: "",
        billPointOfDeliveryName: "",
      },
    });

  const {
    data: pointOfDeliveryData,
    mutate,
    isError,
    isPending,
  } = usePostPointsOfDeliveryFilter();

  const [currentSize, setCurrentSize] = useState(10);

  const onSubmit = ({ page, size }: PointOfDeliveryFormSubmitArguments) => {
    const { pointOfDeliveryNumber, billPointOfDeliveryName } = getValues();
    mutate({
      inputsPayload: {
        pointOfDeliveryNumber: pointOfDeliveryNumber || undefined,
        billPointOfDeliveryName: billPointOfDeliveryName || undefined,
      },
      paginationPayload: {
        page: page ?? 1,
        size: size ?? currentSize,
      },
    });
  };

  const parsedData = useParsePointsOfDeliveryResponse(
    pointOfDeliveryData?.data,
  );

  return (
    <Card className={classes.container}>
      <form
        className={classes.form}
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="billPointOfDeliveryName"
          render={({ field, fieldState }) => (
            <CleanableInput
              {...field}
              className={classes.input}
              data-dd-privacy="allow"
              disabled={isPending}
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("searchDrawer.form.pointOfDelivery.reference.label")}
              placeholder={t(
                "searchDrawer.form.pointOfDelivery.reference.placeholder",
              )}
              size="lg"
              value={field.value ?? ""}
            />
          )}
          rules={{
            deps: ["pointOfDeliveryNumber"],
            validate: (value, values) =>
              Boolean(value) ||
              Boolean(values.pointOfDeliveryNumber) ||
              "form.error.atLeastOneFieldMustBeFilled",
          }}
        />
        <Controller
          control={control}
          name="pointOfDeliveryNumber"
          render={({ field, fieldState }) => (
            <CleanableInput
              {...field}
              className={classes.input}
              data-dd-privacy="allow"
              disabled={isPending}
              hasError={Boolean(fieldState.error)}
              label={t("searchDrawer.form.pointOfDelivery.number.label")}
              placeholder={t(
                "searchDrawer.form.pointOfDelivery.number.placeholder",
              )}
              size="lg"
              value={field.value ?? ""}
            />
          )}
          rules={{
            deps: ["billPointOfDeliveryName"],
            validate: (value, values) =>
              Boolean(value) ||
              Boolean(values.billPointOfDeliveryName) ||
              "form.error.atLeastOneFieldMustBeFilled",
          }}
        />
      </form>
      <SearchResults
        data={parsedData}
        isError={isError}
        isLoading={isPending}
        level="pointOfDelivery"
        submitPageChange={(page) => onSubmit({ page })}
        submitSizeChange={(size) => {
          setCurrentSize(size);
          onSubmit({ size });
        }}
        toggleDrawer={toggleDrawer}
      />
    </Card>
  );
};
